import { useCallback, useEffect, useState, type DependencyList, type RefObject } from 'react';

const DEFAULT_SCROLL_BOTTOM_THRESHOLD_PX = 24;
const MOBILE_BREAKPOINT_PX = 992;

const isMobileViewport = (): boolean => {
  return typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT_PX;
};

const hasBottomScrollSpace = (
  container: HTMLDivElement | null,
  thresholdPx: number = DEFAULT_SCROLL_BOTTOM_THRESHOLD_PX
): boolean => {
  if (isMobileViewport()) {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const scrollHeight = document.documentElement.scrollHeight;
    const clientHeight = window.innerHeight;
    const remainingScroll = scrollHeight - clientHeight - scrollTop;
    return remainingScroll > thresholdPx;
  }

  if (!container) return false;
  const remainingScroll = container.scrollHeight - container.clientHeight - container.scrollTop;
  return remainingScroll > thresholdPx;
};

const scrollContainerToBottom = (container: HTMLDivElement | null, behavior: ScrollBehavior = 'smooth'): void => {
  if (isMobileViewport()) {
    window.scrollTo({
      top: document.documentElement.scrollHeight,
      behavior,
    });
    return;
  }

  if (!container) return;
  container.scrollTo({
    top: container.scrollHeight,
    behavior,
  });
};

interface UseScrollToBottomIndicatorResult {
  showScrollToBottom: boolean;
  scrollToBottom: () => void;
}

/**
 * Tracks if there is remaining content below the fold and provides
 * a "scroll to bottom" action for the chat messages container.
 * On mobile (<992px), uses window scroll instead of container scroll.
 */
export const useScrollToBottomIndicator = (
  containerRef: RefObject<HTMLDivElement>,
  deps: DependencyList = []
): UseScrollToBottomIndicatorResult => {
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);

  const updateVisibility = useCallback(() => {
    const container = containerRef.current;
    setShowScrollToBottom(hasBottomScrollSpace(container));
  }, [containerRef]);

  const scrollToBottom = useCallback(() => {
    const container = containerRef.current;
    scrollContainerToBottom(container);
  }, [containerRef]);

  useEffect(() => {
    const container = containerRef.current;

    updateVisibility();

    const onScroll = () => updateVisibility();

    if (isMobileViewport()) {
      window.addEventListener('scroll', onScroll, { passive: true });
    } else if (container) {
      container.addEventListener('scroll', onScroll, { passive: true });
    }

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined' && container) {
      resizeObserver = new ResizeObserver(() => updateVisibility());
      resizeObserver.observe(container);
    }

    const onResize = () => updateVisibility();
    window.addEventListener('resize', onResize, { passive: true });

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      if (container) {
        container.removeEventListener('scroll', onScroll);
      }
      resizeObserver?.disconnect();
    };
  }, [containerRef, updateVisibility]);

  useEffect(() => {
    requestAnimationFrame(() => {
      updateVisibility();
    });
  }, [...deps, updateVisibility]);

  return {
    showScrollToBottom,
    scrollToBottom,
  };
};
